import { useReactOidc } from '@axa-fr/react-oidc-context'
import { LoadingPage } from '@panwds/react-ui'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LoadCSPAccounts, LoadInstances, LoadLicense } from '../common/actions/GlobalActions'
import {
  CTX_CDL,
  APP_ID_PRISMA_ACCESS_EDITION,
  LICENSE_CONTEXT_TO_APP_ID,
  APP_ID_NG_CASB,
  ENTERPRISE_FLOW_APPS,
  APP_ID_FEDRAMP,
  LICENSE_TYPE_FED_MOD,
  LICENSE_TYPE_FED_HIGH,
} from '../common/constants/GlobalConstants'
import { ARRAY, isNonemptyArray } from '../common/utils/Utils'
import {
  ACTIVATION_FORK,
  ALLOWED_LICENSE_CONTEXT,
  ENTERPRISE,
  PANORAMA_SPLASH,
  USER_FLOW_SINGLE,
  USER_FLOW_MULTI,
  MSP,
} from './components/constants/SubscriptionConstant'
import EnterpriseActivation from './components/EnterpriseActivation/EnterpriseActivation'
import SplashScreen from './components/splashScreen/SplashScreen'
import { ROOT_NODE_ID } from '../tenancy/constants/TenancyConstants'
import { history, getVarValue } from '../common/utils/Sparky'
import {
  FF_SHOW_SPLASH_SCREEN,
  FF_AUTH_SUPPORT_ACCOUNTS,
  FF_STANDALONE_CDL,
  FF_FEDRAMP_HIGH,
  FF_FEDRAMP_MOD,
  FF_COMMERCIAL,
  FF_UNIFIED_LICENSE_TABLE,
  FF_HIDE_SASE_TABLE
} from '../common/utils/FeatureFlags'
import { loadCSPAccounts } from '../common/reducers/actions/LicenseReducer.actions'
import SubscriptionTable from './components/subscriptionTable/SubscriptionTable'
import ActivationFlowFork from './components/splashScreen/ActivationFlowFork'
import { isPanoramaEnabled } from '../common/reducers/actions/Subscription.actions'
import SomethingWentWrong from './components/splashScreen/SomethingWentWrong'
import OneAppSubscriptionPage from '../activation/components/OneAppSubscriptionPage'
import ActivationTilePage from '../activation/components/ActivationTilePage'
import { isVMFlexActivationEnabled } from '../common/utils/Utils'

const useAuthServiceAccounts = getVarValue(FF_AUTH_SUPPORT_ACCOUNTS, false)
const fedHighEnv = getVarValue(FF_FEDRAMP_HIGH, false)
// default true, turn off on prod fedramp high
const fedModEnv = getVarValue(FF_FEDRAMP_MOD, true)
const commerceEnv = getVarValue(FF_COMMERCIAL, true)

const enableUnifiedLicenseTable = getVarValue(FF_UNIFIED_LICENSE_TABLE, false)
const hideSaseTable = getVarValue(FF_HIDE_SASE_TABLE, false)
// const enableProfileActivation = getVarValue(FF_ENABLE_PROFILE_ACTIVATION, false)

/*
 * Created By sswaminathan
 * Created on Mon Nov 22 2021
 * Created on index.js
 * Copyright (c) 2021  ©Palo Alto Networks
 */

const TSG_NONE = 'none'

const SaseSubscription = (props) => {
  const { globalTsgId, flattenTree, auth, dispatch, licenseReducer, subscriptionReducer, setSaseTableDisplayed } = props
  const { dispatch: licenseDispatch } = props
  setSaseTableDisplayed(false)
  const { isPanoramaLocked } = subscriptionReducer
  const [tableColumnData, setTableColumnData] = useState([])
  const showSplashScreen = getVarValue(FF_SHOW_SPLASH_SCREEN, false)
  const { oidcUser } = useReactOidc()
  const [pageProps, setPageProps] = useState({ pageIndex: 0, pageSize: 10 })
  const [activationFlow, setActivationFlow] = useState(null)
  const [isLoading, setLoading] = useState(true)
  const [isLicenseLoaded, setLicenseLoaded] = useState(false)
  const [cspData, setCspData] = useState([])
  const [errorDescription, setErrorDescription] = useState(null)

  // retrieved user flow from session storage
  //const user_flow_type = sessionStorage.getItem(USER_FLOW)
  const user_flow_type = licenseReducer?.userFlow

  const [licenseContext, licenseHash, urlContext] = useMemo(() => {
    // curent path will be /setup/context/hash
    const [base, licenseCtx, hash] = history.location.pathname.replace(/^\/(?:hub\/)?/, '').split('/')

    if (base !== 'setup') {
      // module opened from other pages
      return ['prisma_access', undefined, null]
    }
    if (licenseCtx === APP_ID_NG_CASB) {
      return [licenseCtx, hash, licenseCtx]
    }
    else if (ALLOWED_LICENSE_CONTEXT.includes(licenseCtx) &&
      (licenseCtx !== CTX_CDL || getVarValue(FF_STANDALONE_CDL, false))) {
      return [licenseCtx, hash, licenseCtx]
    }
    else {
      const isPrismaAccess = licenseReducer.licenseDetails?.some(({ licenses }) => {
        return licenses.some(({ app_id }) => {
          return app_id === APP_ID_PRISMA_ACCESS_EDITION
        })
      })
      if (licenseReducer.licenseDetails?.length === 0 || isPrismaAccess) {
        return ['prisma_access', hash, null]
      }
      else {
        return ['cgx', hash, null]
      }
    }
  }, [licenseReducer.licenseDetails])

  /**
   * user_flow_type and activationFlow
   */


  // set different flow and remember for session
  /**
   * Flow decision logic for tsg:none users
   * 1. User comes via tsg:none, That time we have to show ActivationFlow fork
   * 2. Once User make choice for single or Multi we store that in Session Storage. @user_flow_type var
   * 3. For tsg:none we are hiding Tenant Management, IAM, DAS
   * 4. User have to create tsg via either claiming license (MT) or Activate license on (ST) flow
   * 5. After step 4. User will have some tsg_id but flow is still decided whatever user has chooses earlier @user_flow_type var
   * 6. Session storage will be clear on the following conditions
   *    6.1 User close the browser
   *    6.2 User close global module
   *    6.3 User reload/refresh the page
   *    6.4 User log off
   */


  /**
   * Normal User flow
   * 1. User just logged without magic link session storage is empty.
   * 2. Flow will decide based on hierarchy for tree user have. That means not children to tsg. All flatten
   * 3. User comes via magic link and session storage is empty.
   *    3.1 We check hierarchy first for ST or MT
   *    3.2 If ST then will land user on ST flow
   *    3.3 if MT then will show user panorama splash screen
   *    3.4 Panorama splash screen have two choices for ST or MT
   *    3.5 choice will be store in session storage at @user_flow_type var
   * 4. Session storage will be clear on the following conditions
   *    4.1 User close the browser
   *    4.2 User close global module
   *    4.3 User reload/refresh the page
   *    4.4 User log off
   */

  useEffect(() => {
    // decide flow based on user choice
    if (user_flow_type === USER_FLOW_SINGLE) {
      setActivationFlow(ENTERPRISE)
    }
    else if (user_flow_type === USER_FLOW_MULTI) {
      setActivationFlow(MSP)
    }
    // decide flow based users condition
    else {
      const tree = flattenTree?.filter(({ id, children }) => id !== ROOT_NODE_ID && Boolean(children?.length))
      if (globalTsgId === TSG_NONE) {
        if (ENTERPRISE_FLOW_APPS.has(urlContext)) {
          setActivationFlow(ENTERPRISE)
        }
        else {
          setActivationFlow(ACTIVATION_FORK)
        }
      }
      else if (cspData.length && flattenTree.length) {
        if (ENTERPRISE_FLOW_APPS.has(licenseContext)) {
          setActivationFlow(ENTERPRISE)
        }
        else if ((urlContext === 'cgx' || licenseContext === 'cgx')) {
          if (tree.length > 0) {
            setActivationFlow(MSP)
          }
          else {
            setActivationFlow(ENTERPRISE)
          }
        }
        else if (urlContext === 'prisma_access') {
          if (licenseHash) {
            if (tree.length && !user_flow_type?.length) {
              setActivationFlow(PANORAMA_SPLASH)
            }
            else {
              setActivationFlow(ENTERPRISE)
            }
          }
        }
      }
      // user have url context as prisma access
      else {
        if (tree.length) {
          setActivationFlow(MSP)
        }
        else {
          setActivationFlow(ENTERPRISE)
        }
      }
    }
  }, [cspData.length, flattenTree, urlContext, licenseContext, showSplashScreen, globalTsgId, user_flow_type, licenseHash])

  /*  useEffect(() => {
    if (!licenseHash || user_flow_type === USER_FLOW_MULTI) {
      setActivationFlow(MSP)
    }
    else {
      licenseReducer.licenseDetails?.length && licenseReducer.licenseDetails.forEach((each) => {
        (each.hash === licenseHash && each.is_claimed) && setActivationFlow(MSP)
      })
    }
  }, [licenseReducer.licenseDetails, licenseHash, user_flow_type])*/
  // load license
  useEffect(() => {
    if (activationFlow) {
      (async () => {
        try {
          setLoading(true)
          LoadLicense({
            tsg_id: globalTsgId, hash: licenseHash,
            flow_type: activationFlow === MSP ? MSP : ENTERPRISE,
            license_context: licenseContext,
          }, licenseDispatch, setLicenseLoaded)
        }
        catch (e) {
          setLoading(false)
        }
      })()
    }
  }, [globalTsgId, licenseContext, licenseHash, licenseDispatch, activationFlow])

  useEffect(() => {
    try {
      globalTsgId !== TSG_NONE.id && LoadInstances({
        tsg_id: props.selectedTreeNode ? props.selectedTreeNode.id : globalTsgId
      }, licenseDispatch)
    }
    catch (e) {
      setLoading(false)
    }
  }, [globalTsgId, licenseDispatch, props.selectedTreeNode])

  const setPanorama = useCallback(({ isPanoramaLocked }) => {
    dispatch(isPanoramaEnabled({ isPanoramaLocked }))
  }, [dispatch])

  const loadCspAccountsByMerge = useCallback(() => {
    try {
      const cspAccounts = LoadCSPAccounts({
        tsg_id: TSG_NONE.id,
        email: oidcUser?.profile?.email
      }, licenseDispatch, false)
      cspAccounts.then((data) => {
        setCspData(data)
        licenseDispatch(loadCSPAccounts(data || [], false))
      })
    }
    catch (e) {
      setLoading(false)
    }
  }, [licenseDispatch, oidcUser?.profile?.email])

  useEffect(() => {
    if (useAuthServiceAccounts && ARRAY.hasArray(auth?.user?.supportAccountIds)) {
      const supportAccountIds = ARRAY.clean(auth?.user?.supportAccountIds)
      licenseDispatch(loadCSPAccounts(supportAccountIds, true))
      setCspData(supportAccountIds)
      supportAccountIds.map(({ name, accountid }) => ({
        value: [name, '-', accountid].join(),
        description: name,
        id: accountid
      }))
    }
    else if (!useAuthServiceAccounts) {
      loadCspAccountsByMerge()
    }
  }, [auth, licenseDispatch, loadCspAccountsByMerge])

  useEffect(() => {
    const result = []
    if (licenseReducer?.licenseTableFormat) {
      result.push(...licenseReducer.licenseTableFormat)
    }
    if (isLicenseLoaded) {
      setLoading(false)
    }
    result.forEach((each) => {
      each.claimed_by_tenant_name = props.sgKeyBy[each.claimed_by_tenant] ? props.sgKeyBy[each.claimed_by_tenant]?.display_name : ''
    })
    setTableColumnData(result)
  }, [isLicenseLoaded, licenseReducer.licenseTableFormat, props.sgKeyBy])

  const onPaginationChange = useCallback(async (props) => {
    const { pageIndex, pageSize } = props
    setPageProps({ pageIndex, pageSize })
  }, [setPageProps])

  const unactivatedLicenses = useMemo(() => {
    if (isNonemptyArray(licenseReducer?.licenseDetails)) {
      return licenseReducer?.licenseDetails?.filter(license => (license.hasOwnProperty('is_activated') && !license.is_activated) || license.is_upgrade)
    }
    return []
  }, [licenseReducer?.licenseDetails])

  const primaryLicense = useMemo(() => {
    const appId = LICENSE_CONTEXT_TO_APP_ID[licenseContext]
    if (ARRAY.hasArray(unactivatedLicenses) && ARRAY.hasArray(unactivatedLicenses[0]?.licenses) && appId) {
      const paeLicense = unactivatedLicenses[0]?.licenses.find(each => each.app_id === APP_ID_PRISMA_ACCESS_EDITION)
      const fedrampAddon = paeLicense?.entitlements?.find(each => each.app_id === APP_ID_FEDRAMP)
      if (paeLicense && fedrampAddon) {
        // fed high magic link, but not fed high supported env
        if (fedrampAddon.license_type.toUpperCase() === LICENSE_TYPE_FED_HIGH && !fedHighEnv) {
          setErrorDescription('You are opening an activation that\'s not supported in this environment')
          return null
        }
        // fed mod magic link, but not fed mod supported env
        if (fedrampAddon.license_type.toUpperCase() === LICENSE_TYPE_FED_MOD && !fedModEnv) {
          setErrorDescription('You are opening an activation that\'s not supported in this environment')
          return null
        }
      }
      else if (!commerceEnv) {
        setErrorDescription('You are opening an activation that\'s not supported in this environment')
        return null
      }
      // need to remove '_onprem' for amend case for panorama activation
      return unactivatedLicenses[0]?.licenses.find(license => license.app_id.replace('_onprem', '') === appId)
    }
    return null
  }, [unactivatedLicenses, licenseContext])

  const isAmend = useMemo(() => {
    return Boolean(primaryLicense?.tsg_id)
  }, [primaryLicense])

  // lock panorama if license has IoT addon
  useEffect(() => {
    if (primaryLicense?.entitlements?.some(entitlement => entitlement?.app_id === 'zingbox')) {
      setPanorama({ isPanoramaLocked: true })
    }
  }, [primaryLicense, setPanorama])

  const handleFlowType = useCallback(({ flowType }) => {
    if (flowType === USER_FLOW_SINGLE) {
      setActivationFlow(ENTERPRISE)
    }
    else {
      setActivationFlow(PANORAMA_SPLASH)
    }
  }, [])

  /*console.log('isLoading', isLoading)
  console.log('actvationflow', activationFlow)
  console.log('user_flow_type', user_flow_type)
  console.log('global_tsg', globalTsgId)*/
  if (isLoading) {
    return <LoadingPage />
  }
  else if (activationFlow === ACTIVATION_FORK) {
    return <ActivationFlowFork handleFlowType={handleFlowType} licenseDispatch={licenseDispatch} />
  }
  else if (activationFlow === ENTERPRISE) {
    return <EnterpriseActivation
      tenants={flattenTree}
      licenses={unactivatedLicenses}
      setPanorama={setPanorama}
      {...{
        ...licenseReducer,
        licenseDispatch,
        licenseHash,
        licenseContext,
        isPanoramaLocked,
        primaryLicense,
        isAmend,
        errorDescription,
        globalTsgId, ...props,
      }} />
  }
  else if (activationFlow === PANORAMA_SPLASH) {
    return <SplashScreen
      setPanorama={setPanorama}
      dispatch={dispatch}
      licenseDispatch={licenseDispatch}
      setActivationFlow={setActivationFlow}
    />
  }
  else if (activationFlow === ACTIVATION_FORK) {
    return <ActivationFlowFork handleFlowType={handleFlowType} licenseDispatch={licenseDispatch} />
  }
  else if (activationFlow === MSP || urlContext === 'cgx') {
    setSaseTableDisplayed(true)
    return <SubscriptionTable
      tableColumnData={tableColumnData}
      pageSize={pageProps.pageSize}
      pageIndex={pageProps.pageIndex}
      {...{
        ...licenseReducer, ...props,
        licenseContext,
        licenseHash,
        activationFlow,
        licenseDispatch,
        isLoading,
        onPaginationChange,
      }}
    />
  }
  else {
    return <SomethingWentWrong />
  }
}

const Subscription = (props) => {
  const [isSaseTableDisplayed, setSaseTableDisplayed] = useState(false)
  const enableProfileActivation = isVMFlexActivationEnabled(props?.auth?.user?.supportAccountIds)
  /**
   * when oidcClient is sase, show sase table + conditionally unified table
   * when oidcClient is netsec, show unified table only
   */
  return (
    <>
      {enableProfileActivation && (<ActivationTilePage {...props}></ActivationTilePage>)}
      {!hideSaseTable && <SaseSubscription setSaseTableDisplayed={setSaseTableDisplayed} {...props} />}
      {((isSaseTableDisplayed || hideSaseTable) && enableUnifiedLicenseTable) && <OneAppSubscriptionPage {...props} />}
    </>
  )

}

export default Subscription

